/**
 * Terrain — the biome's parameters to a per-cell height field and its materials.
 *
 * The whole 32x32 grid is land. This is a tapestry, not a side view: the creature
 * stands *on* the field rather than in front of a horizon, which is the reading
 * Terraforms established and the one the palette gate was measured against (every
 * plumage-versus-terrain rule assumes the bird overlaps ground, not sky).
 *
 * The expensive part of an on-chain renderer is fBm, and this file evaluates it
 * exactly **once per cell** no matter what the biome asks for:
 *
 * - Warping displaces the *sample point* before the evaluation (`warpPoint`), so it
 *   costs two cheap `value2d` calls rather than a second fBm.
 * - The ridged field is `fold()` of the smooth one, so blending toward crests reuses
 *   the sample already in hand. Calling `ridged()` here instead would have doubled
 *   the cost of every ridged biome for an identical result.
 * - Relief and vignette are integer post-processes on the finished height.
 *
 * That gives 1024 fBm evaluations for a token, which is the floor for this design.
 *
 * Pipeline, in order, and the order matters:
 *
 *   sample -> ridge blend -> relief -> vignette -> water test -> band
 *
 * Relief before vignette because relief shapes *what the terrain is* (the histogram
 * of heights: cliffs or rolling ground) while vignette shapes *where it sits in the
 * frame*. Reversing them would let the frame's falloff be stretched back out by a
 * high-relief biome, and the framing is the thing that has to survive.
 */

import { CELL_COUNT, ELEVATION_LEVELS, GRID_COLS, GRID_ROWS, ONE } from "../constants.js";
import type { Biome } from "../data/biomes.js";
import { clamp, iabs, idiv, shr } from "../engine/int.js";
import type { FbmParams } from "../engine/noise.js";
import { fbm, fold, warpPoint } from "../engine/noise.js";
import { MAT_WATER, terrainMaterial } from "./materials.js";
import { cellIndex } from "./cell.js";

/** Height field midpoint, the axis `relief` expands about. */
const MID_HEIGHT = 32768;
/** Height field ceiling. Noise is `[0, 65535]` and stays there. */
const MAX_HEIGHT = 65535;

/** Squared radius of the furthest cell centre from the grid centre, in half-cells. */
const MAX_R2 = (GRID_COLS - 1) * (GRID_COLS - 1) + (GRID_ROWS - 1) * (GRID_ROWS - 1);

export interface Terrain {
  /** Post-processed height per cell, `[0, 65535]`, row-major. */
  readonly height: Uint16Array;
  /** Material per cell: `MAT_WATER` or a land band. Row-major. */
  readonly material: Uint8Array;
  /** How many cells came out as water. Used for the "Water" metadata trait. */
  readonly waterCells: number;
  /** Which of the `ELEVATION_LEVELS` bands the token actually used. */
  readonly bandsUsed: number;
}

/**
 * Normalised squared distance from the grid centre, 0 at the centre and 256 at the
 * furthest corner.
 *
 * Squared rather than true radius on purpose: a squared falloff has a flat middle and
 * a fast edge, which is exactly the shape a frame wants. It also keeps the whole
 * calculation in integers with no square root to mirror in Solidity.
 *
 * Coordinates are doubled so the centre of a 32-wide grid lands between cells without
 * a fractional term. Doubling does not change the *maximum*: `|2x - (COLS-1)|` peaks
 * at `COLS-1` on both edges, so `MAX_R2` is the right divisor and the first version of
 * this function, which divided by `MAX_R2 * 4`, silently ran every biome's vignette at
 * a quarter of its authored strength. `tools/terraincheck.ts` found it by reporting a
 * corner mean of zero for all nine biomes.
 */
export function radialFalloff(x: number, y: number): number {
  const dx = iabs(2 * x - (GRID_COLS - 1));
  const dy = iabs(2 * y - (GRID_ROWS - 1));
  return idiv((dx * dx + dy * dy) * 256, MAX_R2);
}

/** Expand or flatten a height about the midpoint. `relief` is 256 for identity. */
export function applyRelief(h: number, relief: number): number {
  if (relief === 256) return h;
  return clamp(MID_HEIGHT + idiv((h - MID_HEIGHT) * relief, 256), 0, MAX_HEIGHT);
}

/**
 * Pull height down radially. Positive `vignette` drops the corners, negative drops
 * the centre; both only ever *reduce* a height, which is what keeps the field inside
 * `[0, 65535]` without a clamp.
 */
export function applyVignette(h: number, vignette: number, falloff: number): number {
  if (vignette === 0) return h;
  const weight = vignette > 0 ? falloff : 256 - falloff;
  const cut = idiv(h * iabs(vignette) * weight, 65536);
  return h - cut;
}

/** Which land band a height falls in, given the biome's water line. */
export function bandOf(h: number, waterLevel: number): number {
  const span = 65536 - waterLevel;
  const band = idiv((h - waterLevel) * ELEVATION_LEVELS, span);
  return clamp(band, 0, ELEVATION_LEVELS - 1);
}

/**
 * Build one token's terrain.
 *
 * `seed` is a 32-bit word drawn from the token's `terrain` stream. `biome.frequency`
 * is Q16.16 noise units per grid cell, so a cell's sample coordinate is just
 * `cell * frequency` and the field is always sampled from the non-negative quadrant.
 */
export function buildTerrain(biome: Biome, seed: number): Terrain {
  const height = new Uint16Array(CELL_COUNT);
  const material = new Uint8Array(CELL_COUNT);
  const params: FbmParams = {
    octaves: biome.octaves,
    gain: biome.gain,
    lacunarity: biome.lacunarity,
  };

  const seenBand = new Array<boolean>(ELEVATION_LEVELS).fill(false);
  let waterCells = 0;

  for (let y = 0; y < GRID_ROWS; y++) {
    for (let x = 0; x < GRID_COLS; x++) {
      let sx = x * biome.frequency;
      let sy = y * biome.frequency;
      if (biome.warp > 0) {
        const p = warpPoint(sx, sy, seed, biome.warp);
        sx = p.x;
        sy = p.y;
      } else {
        // Match warpPoint's 8-cell bias so a biome's shapes do not shift when its
        // warp is authored to zero. Without this, `warp: 0` would sample a different
        // region of the field than `warp: 1`, and the two would look unrelated.
        sx += 8 * ONE;
        sy += 8 * ONE;
      }

      const smooth = fbm(sx, sy, seed, params);
      let h = smooth;
      if (biome.ridge === 256) {
        h = fold(smooth);
      } else if (biome.ridge > 0) {
        h = smooth + idiv((fold(smooth) - smooth) * biome.ridge, 256);
      }

      h = applyRelief(h, biome.relief);
      h = applyVignette(h, biome.vignette, radialFalloff(x, y));

      const idx = cellIndex(x, y);
      height[idx] = h;
      if (h < biome.waterLevel) {
        material[idx] = MAT_WATER;
        waterCells++;
      } else {
        const band = bandOf(h, biome.waterLevel);
        material[idx] = terrainMaterial(band);
        seenBand[band] = true;
      }
    }
  }

  return {
    height,
    material,
    waterCells,
    bandsUsed: seenBand.filter(Boolean).length,
  };
}

/**
 * Ground line: the row the creature's feet stand on.
 *
 * The bird is placed against the lower third of the frame, which is where a standing
 * subject reads as standing rather than floating, and the terrain behind it is left
 * alone. Taking this from the grid rather than from the height field is deliberate —
 * a foot that tracked local elevation would sink into a valley on half the tokens and
 * the collection would lose its shared composition.
 */
export const GROUND_ROW = GRID_ROWS - 4;

/**
 * Mean height over the band of rows the creature occupies.
 *
 * Used to decide which of the three plumage tones leads on a given token: a bird on
 * pale high ground takes its darker feathers forward, one on dark low ground takes
 * its lighter ones. This is the only place the terrain talks back to the creature.
 */
export function backdropHeight(t: Terrain, topRow: number, bottomRow: number): number {
  const from = clamp(topRow, 0, GRID_ROWS - 1);
  const to = clamp(bottomRow, from, GRID_ROWS - 1);
  let sum = 0;
  let n = 0;
  for (let y = from; y <= to; y++) {
    for (let x = 0; x < GRID_COLS; x++) {
      sum += t.height[cellIndex(x, y)] as number;
      n++;
    }
  }
  return n === 0 ? MID_HEIGHT : idiv(sum, n);
}

/** Structural guard invoked by tests. */
export function validateTerrain(): void {
  if (radialFalloff((GRID_COLS - 1) >> 1, (GRID_ROWS - 1) >> 1) > 4) {
    throw new Error("the vignette is not centred on the grid");
  }
  if (radialFalloff(0, 0) !== 256) throw new Error("the vignette does not reach 256 at the corner");
  if (radialFalloff(GRID_COLS - 1, GRID_ROWS - 1) !== 256) {
    throw new Error("the vignette is not symmetric across the grid");
  }
  if (applyRelief(MID_HEIGHT, 512) !== MID_HEIGHT) throw new Error("relief moved the midpoint");
  if (applyRelief(MAX_HEIGHT, 512) !== MAX_HEIGHT) throw new Error("relief overran the ceiling");
  if (applyVignette(MAX_HEIGHT, 256, 256) !== 0) throw new Error("a full vignette must reach zero");
  if (applyVignette(MAX_HEIGHT, -256, 0) !== 0) throw new Error("an inverted vignette must reach zero");
  if (bandOf(0, 0) !== 0) throw new Error("the lowest height is not band 0");
  if (bandOf(MAX_HEIGHT, 0) !== ELEVATION_LEVELS - 1) throw new Error("the highest height is not the top band");
  if (GROUND_ROW >= GRID_ROWS || GROUND_ROW < (GRID_ROWS * 2) / 3) {
    throw new Error("the ground line is not in the lower third of the frame");
  }
  if (shr(MAX_HEIGHT, 16) !== 0) throw new Error("heights must fit in sixteen bits");
}
